import type { Order, OrderFieldSpecs, OrderOutdated, UnitMission } from "../../api/types";
import { t } from "../../i18n/i18n";

/** Paragraphes « à revoir » de l'ordre (docs/18 §5.4, §6.7) : modifiés à la main, et dont le texte généré a changé
 *  depuis (composition, rôles, Données initiales…). Fonctions pures, testées par outdated.test.ts. */

/** Champs à revoir : ceux que le serveur signale et qui sont toujours modifiés à la main, dans l'ordre des specs ;
 *  un champ absent des specs (paragraphe que l'ordre de ce niveau n'imprime pas : annexe de la division) est ignoré. */
export function outdatedFields(order: Pick<Order, "outdated" | "edited" | "status">, specs?: OrderFieldSpecs | null): string[] {
  if (order.status === "finalized") return [];
  const flagged = Object.keys(order.outdated ?? {}).filter((key) => order.edited.includes(key));
  if (!specs) return flagged;
  const position = new Map(specs.fields.map((f, i) => [f.key, i]));
  return flagged.filter((key) => position.has(key)).sort((a, b) => position.get(a)! - position.get(b)!);
}

/** Paragraphes modifiés à la main que l'éditeur montre : ceux des specs (tous tant qu'elles ne sont pas lues). */
export function editedFields(order: Pick<Order, "edited">, specs?: OrderFieldSpecs | null): string[] {
  if (!specs) return order.edited;
  const keys = new Set(specs.fields.map((f) => f.key));
  return order.edited.filter((key) => keys.has(key));
}

/** Nombre de paragraphes à revoir par onglet de l'éditeur. */
export function outdatedByTab(fields: readonly string[], specs: OrderFieldSpecs): Map<number, number> {
  const tabOf = new Map(specs.fields.map((f) => [f.key, f.tab]));
  const out = new Map<number, number>();
  for (const key of fields) {
    const tab = tabOf.get(key);
    if (tab !== undefined) out.set(tab, (out.get(tab) ?? 0) + 1);
  }
  return out;
}

/** Ligne « - nom : texte » d'une liste comparée, à la typographie de la langue de l'interface (« - name: text »). */
function item(name: string, text: string): string {
  return `- ${t("{label} : {text}", { label: name, text })}`;
}

/** Texte comparable d'une valeur de champ : texte tel quel, missions du §3.b une par ligne, matrice ou articulation
 *  en lignes lisibles, autre valeur en JSON. */
export function fieldText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    if (value.startsWith("{") && value.includes('"rows"')) {
      try {
        const matrix = JSON.parse(value) as { rows?: { displayName?: string; cells?: string[] }[]; emd?: string };
        const rows = (matrix.rows ?? []).map((r) => item(r.displayName ?? "", (r.cells ?? []).join(" / ")));
        const emd = matrix.emd ? t("{label} : {text}", { label: t("EMD"), text: matrix.emd }) : "";
        return [...rows, emd].filter(Boolean).join("\n");
      } catch {
        return value;
      }
    }
    // Articulation de la division : « nom\tE|-\tligne1␞ligne2 ».
    if (value.includes("\t") && value.includes("\x1e")) {
      return value
        .split("\n")
        .map((row) => {
          const [name, , rest] = row.split("\t");
          return item(name ?? "", (rest ?? "").split("\x1e").join(t(" ; ")));
        })
        .join("\n");
    }
    return value;
  }
  if (Array.isArray(value)) {
    return (value as Partial<UnitMission>[])
      .map((m) =>
        m && typeof m === "object" && "displayName" in m
          ? item(m.displayName ?? "", m.mission || m.missionT1 || "")
          : JSON.stringify(m),
      )
      .join("\n");
  }
  return JSON.stringify(value, null, 1);
}

/** Écart d'un champ à revoir : texte de l'utilisateur et texte généré à jour. */
export function outdatedPair(order: Pick<Order, "fields" | "outdated">, key: string): { mine: string; generated: string } | null {
  const entry: OrderOutdated | undefined = order.outdated?.[key];
  if (!entry) return null;
  return { mine: fieldText(order.fields[key]), generated: fieldText(entry.generated) };
}
